import React from 'react';
import { Container, Heading, Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';
import { FontFamily, FontWeight, TextVariant } from '../../styles/typography';
import Button from '../Button/Button';
import { ButtonVariant } from '../../styles/buttons';
import { useDictionary } from '../../hooks/useDictionary';
import { colors } from '../../styles/colors';
import LinkViewModel from '../../view-models/LinkViewModel';

export type PartiesBannerProps = {
  attentionAnalogue?: LinkViewModel & {
    heading?: string;
    description?: string;
  };
};

const headingStyle: Record<string, any> = {
  display: 'block',
  mt: 0,
  mb: rem(16),
  color: colors.base.Text.BrandLighter,
  fontFamily: FontFamily.Heading,
  fontSize: [rem(32), rem(48)],
  fontWeight: FontWeight.Normal,
  lineHeight: [rem(40), rem(56)],
  letterSpacing: rem(1.2),
  textWrap: 'pretty',
};

function PartiesBanner({ attentionAnalogue }: PartiesBannerProps) {
  const { t, exists } = useDictionary();
  const description =
    attentionAnalogue?.description ||
    (exists('attentionAnalogue.description')
      ? t('attentionAnalogue.description')
      : undefined);

  return (
    <Container
      as="section"
      sx={{
        maxWidth: 'unset',
        backgroundColor: colors.components.banner.Background.Default,
        color: colors.base.Text.BrandLighter,
        textAlign: 'center',
      }}
    >
      <Container
        as="article"
        sx={{
          py: [rem(120)],
          px: [rem(20), rem(20), rem(40)],
        }}
      >
        <Heading
          as="h2"
          sx={headingStyle}
        >
          {attentionAnalogue?.heading ?? t('attentionAnalogue.title')}
        </Heading>
        {description && (
          <Text
            variant={TextVariant.BodyLg}
            sx={{
              display: 'block',
              mt: 0,
              mb: rem(40),
              fontSize: rem(20),
            }}
          >
            {description}
          </Text>
        )}
        <Button
          variant={ButtonVariant.Secondary}
          sx={{
            backgroundColor:
              colors.components.button.Tertiary.Background.Default,
            color: colors.components.button.Tertiary.Text.Default,
            fontWeight: FontWeight.Normal,
            letterSpacing: 0,
            lineHeight: rem(16),
            px: rem(32),
            py: rem(16),
            '&:hover': {
              backgroundColor:
                colors.components.button.Tertiary.Background.Hover,
              color: colors.components.button.Tertiary.Text.Hover,
            },
          }}
          href={attentionAnalogue?.href}
        >
          {attentionAnalogue?.label ?? attentionAnalogue?.title}
        </Button>
      </Container>
    </Container>
  );
}

export default PartiesBanner;
